import * as React from 'react';
import type { FilterOptionsResponse } from '@ooh/contracts';
import {
  buildLocationFilterField,
  buildLocationPanelValue,
  adaptLocationFilterChange,
  type FilterFieldLabels,
} from '../lib/filter-fields';

/**
 * Derives the FilterPanel-facing `value`/`onChange` pair for the combined
 * "Lokalizacja" pill: merges the region/county/agglomeration/city/
 * administrativeAreaIds keys into one flat `location` array for display, and
 * redistributes any update on that combined field back into those keys before
 * it reaches the URL-backed filter state managed by `useSharedFilters`.
 */
export function useLocationFilterPanel(
  filterOptionsData: FilterOptionsResponse | undefined,
  filterFieldLabels: FilterFieldLabels,
  filterValue: Record<string, string[]>,
  handleFiltersChange: (value: Record<string, string[]>) => void,
): {
  panelFilterValue: Record<string, string[]>;
  handlePanelFiltersChange: (value: Record<string, string[]>) => void;
} {
  const locationField = React.useMemo(
    () => buildLocationFilterField(filterOptionsData, filterFieldLabels),
    [filterOptionsData, filterFieldLabels],
  );
  const panelFilterValue = React.useMemo(
    () =>
      locationField
        ? { ...filterValue, location: buildLocationPanelValue(filterValue) }
        : filterValue,
    [filterValue, locationField],
  );
  const handlePanelFiltersChange = React.useMemo(
    () => adaptLocationFilterChange(locationField, handleFiltersChange),
    [locationField, handleFiltersChange],
  );
  return { panelFilterValue, handlePanelFiltersChange };
}
